/**
 * Records as Documents locations (#6559): the kinds the page offers, their
 * user-facing names, and the two addresses a record has — its location on the
 * Documents page and its own page in RMS. User-facing copy says record, never
 * entity.
 *
 * The names come from the RMS's own kind map (#6559 round 6) and the glyphs
 * from the RMS's own `EntityIcon` (#6559 round 7d) — a manager is the building
 * the RMS draws, a vehicle its piggy bank. Both were second hardcoded lists
 * here once, which is how Documents came to call an investment vehicle a
 * "Fund" and to draw it as a rising line no other surface uses.
 */
import type { ViewKind } from "@/components/FpPages/RMS/Dashboard/types";
import { entityKindMap } from "@/components/FpPages/RMS/Dashboard/utils";
import { createEntitySlug } from "@/components/FpPages/RMS/Dashboard/utils/slugUtils";
import type { LinkedEntity } from "./api";

export type RecordKind = "manager" | "vehicle" | "company" | "person";

export const RECORD_KINDS: readonly RecordKind[] = [
  "manager",
  "vehicle",
  "company",
  "person",
];

/** A kind the page does not offer falls through to its raw name; the route
 *  refuses those as locations, so this is the tag on a linked record only. */
const kindInfo = (kind: string) =>
  (
    entityKindMap as Partial<
      Record<string, { label: string; displayLabel: string }>
    >
  )[kind];

/** The kind a record's glyph is drawn from. The wire hands us a plain string,
 *  and `EntityIcon` already models the one it cannot draw, so the parse
 *  happens once here rather than as a cast at each render site — a cast would
 *  claim a vocabulary the string has not been checked against. */
export const recordIconKind = (kind: string): ViewKind | "unknown" =>
  kind in entityKindMap ? (kind as ViewKind) : "unknown";

export const recordKindLabel = (kind: string): string =>
  kindInfo(kind)?.label ?? kind;

export const recordKindPlural = (kind: string): string =>
  kindInfo(kind)?.displayLabel ?? kind;

/** The record's location on the Documents page: the id is the whole address. */
export const recordDocumentsPath = (publicId: string): string =>
  `/documents?view=record&record=${encodeURIComponent(publicId)}`;

/** Every record that has documents, the page's one records browse. */
export const RECORDS_INDEX_PATH = "/documents?view=records";

/** The record's own Documents tab in RMS. */
export const recordRmsPath = (record: LinkedEntity): string =>
  `/rms/${record.kind}/${createEntitySlug({ name: record.name, id: record.publicId })}/documents`;

/** A record's document count, said only when it is worth reading (round 19,
 *  Claude Design Q1): nothing at zero, singular at one, and capped past 999.
 *  The count is a convenience, not a fact the row owes the reader. */
export const documentCountLabel = (count: number): string | null => {
  if (count <= 0) return null;
  if (count === 1) return "1 document";
  if (count > 999) return "999+ documents";
  return `${count.toLocaleString()} documents`;
};

/** `<Kind> · <count>`, or the kind alone when the count has nothing to say. */
export const recordMetaLabel = (kind: string, count: number): string =>
  [recordKindLabel(kind), documentCountLabel(count)]
    .filter((part) => part !== null)
    .join(" · ");
